import { SWReactIcons } from "@similarweb/icons";
import { colorsPalettes } from "@similarweb/styles";
import { Accordion } from "app/components/Accordion/Accordion";
import { AccordionHead } from "app/components/Accordion/AccordionHead";
import { translate } from "app/filters/i18nFilter";
import type { FC } from "react";
import { useEffect, useState } from "react";
import { FlexRow } from "pro-features/styled components/StyledFlex/src/StyledFlex";
import { CopyToClipboardButton } from "pro-features/components/Buttons/src/CopyToClipboardButton";
import { PixelPlaceholderLoader } from "@similarweb/ui-components";
import { DefaultFetchService } from "app/services/fetchService";
import Sentry from "@similarweb/sentry-logger";
import { IconButton } from "@similarweb/ui-components";
import { proNavigator } from "single-spa/react-app/ProNavigator";
import { useCurrentStateAndParams } from "@uirouter/react";
import {
    AccordionWrapper,
    ButtonsWrapper,
    InsightsContent,
    InsightsContentWrapper,
    InsightsLoaderWrapper,
    InsightsText,
    InsightsTextWrapper,
    InsightsToggleText,
} from "./StyledComponents";
import { allTrackers } from "app/services/track/track";

const getInsightTextPrefix = () => translate("wa.traffic.engagement.insights.copy_text.prefix");
const NAVIGATE_TO_STATE = "digitalsuite_website_marketingchannels";

const InsightsToggle = () => {
    return (
        <FlexRow align-items="center">
            <SWReactIcons
                iconName="arrow-down"
                iconColor={colorsPalettes.blue[400]}
                size="xs"
            />
            <InsightsToggleText>{translate("wa.traffic.engagement.insights.toggle")}</InsightsToggleText>
        </FlexRow>
    );
};

interface IInsightServerResponse {
    DetailedSummary: string;
}

interface IInsights {
    apiParams: Record<string, string | number>;
}

export const Insights: FC<IInsights> = ({ apiParams }) => {
    const { params } = useCurrentStateAndParams();
    const [isLoading, setIsLoading] = useState(false);
    const [rawData, setRawData] = useState<IInsightServerResponse>(null);
    const [textToCopy, setTextToCopy] = useState("");
    const [isInsightsAccordionOpen, setIsInsightsAccordionOpen] = useState(false);

    const getInsights = async () => {
        setIsLoading(true);
        try {
            const res = await DefaultFetchService.getInstance().get<IInsightServerResponse>(
                "widgetApi/TrafficAndEngagement/OpenAi/Data",
                apiParams,
            );
            setRawData(res);
            allTrackers.dispatchTrackEvent("Button", "click", "Traffic and engagement insights/Insight generated");
        } catch (error) {
            Sentry.error("Error fetching openAi insights in Traffic and engagement", error);
            setRawData(null);
        } finally {
            setIsLoading(false);
        }
    };

    useEffect(() => {
        const text = `${getInsightTextPrefix()} ${rawData?.DetailedSummary}`;
        setTextToCopy(text);
    }, [rawData?.DetailedSummary]);

    const onAccordionToggleClick = () => {
        if (!isLoading && !rawData) {
            getInsights();
        }
        allTrackers.dispatchTrackEvent(
            "Button",
            "click",
            `Traffic and engagement insights/${isInsightsAccordionOpen ? "close" : "open"}`,
        );
        setIsInsightsAccordionOpen((isAccOpen) => !isAccOpen);
    };

    const onNavigateClick = () => {
        allTrackers.dispatchTrackEvent("Button", "click", "Traffic and engagement insights/go to mmx");
        proNavigator.go(NAVIGATE_TO_STATE, params);
    };

    const onCopyToClipboardClick = () =>
        allTrackers.dispatchTrackEvent("Button", "click", "Traffic and engagement insights/copy text");

    return (
        <Accordion
            accordionHeadComponent={
                <AccordionHead
                    isAccordionOpen={isInsightsAccordionOpen}
                    title={translate("wa.traffic.engagement.insights.title")}
                    titleIconName={"sparkles-ai"}
                    titleIconColor={colorsPalettes.carbon[500]}
                    titleIconSize="sm"
                    headerContent={<InsightsToggle />}
                />
            }
            isOpen={isInsightsAccordionOpen}
            onClick={onAccordionToggleClick}
            showToggleIcon={false}
            wrapperComponent={AccordionWrapper}
        >
            <InsightsContentWrapper>
                <InsightsContent>
                    <InsightsTextWrapper>
                        {isLoading ? (
                            <InsightsLoaderWrapper>
                                <PixelPlaceholderLoader
                                    height={6}
                                    width="95%"
                                />
                                <PixelPlaceholderLoader
                                    height={6}
                                    width="75%"
                                />
                            </InsightsLoaderWrapper>
                        ) : (
                            <InsightsText>{rawData?.DetailedSummary}</InsightsText>
                        )}
                    </InsightsTextWrapper>
                    <ButtonsWrapper>
                        <IconButton
                            iconName="arrow-right"
                            type="outlined"
                            onClick={onNavigateClick}
                            isDisabled={isLoading}
                        >
                            {translate("wa.traffic.engagement.insights.button.navigation")}
                        </IconButton>
                        <CopyToClipboardButton
                            textToCopy={textToCopy}
                            buttonType="flat"
                            isDisabled={isLoading}
                            onCopyToClipboardClick={onCopyToClipboardClick}
                        />
                    </ButtonsWrapper>
                </InsightsContent>
            </InsightsContentWrapper>
        </Accordion>
    );
};
